import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, ArrowUpRight, EnvelopeSimple, Briefcase } from "@phosphor-icons/react";

const BLANK = {
  email: "",
  first_name: "",
  last_name: "",
  business_name: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  town: "",
  county: "Clare",
  eircode: "",
  vat_number: "",
  send_welcome_email: true,
  create_draft_job: false,
  draft_project_type: "",
  draft_project_name: "",
};

export default function NewCustomerDrawer({ open, onClose, onCreated }) {
  const [form, setForm] = useState(BLANK);
  const [counties, setCounties] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/auth/counties").then(({ data }) => setCounties(data)).catch(() => {});
    api.get("/constants").then(({ data }) => setProjectTypes(data.project_types || [])).catch(() => {});
    setForm(BLANK);
    setErr("");
  }, [open]);

  if (!open) return null;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const { data } = await api.post("/admin/customers", form);
      const name = `${form.first_name} ${form.last_name}`.trim();
      let msg = form.send_welcome_email
        ? `${name} created — welcome email sent to ${form.email}`
        : `${name} created (no email sent)`;
      if (data.draft_job) msg += ` · draft job ${data.draft_job.job_number} added`;
      toast.success(msg);
      onCreated?.(data);
      onClose();
    } catch (ex) {
      setErr(formatApiError(ex.response?.data?.detail) || "Failed to create customer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      data-testid="new-customer-drawer"
    >
      {/* backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      {/* panel */}
      <form
        onSubmit={submit}
        className="relative w-full max-w-xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col"
      >
        <div className="sticky top-0 z-10 bg-white border-b border-[var(--gc-border)] px-8 py-6 flex items-center justify-between">
          <div>
            <div className="eyebrow mb-1">Admin</div>
            <h2 className="font-display text-3xl">New customer</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-[var(--gc-bg-alt)] rounded"
            data-testid="new-customer-close"
            aria-label="Close"
          >
            <X size={20} weight="bold" />
          </button>
        </div>

        <div className="px-8 py-8 space-y-8 flex-1">
          {err && (
            <div className="border-l-2 border-[var(--gc-danger)] bg-red-50 text-[var(--gc-danger)] p-3 text-sm" data-testid="new-customer-error">
              {err}
            </div>
          )}

          {/* Contact */}
          <div className="grid grid-cols-2 gap-5">
            <Field label="First name" required>
              <input required value={form.first_name} onChange={set("first_name")} className="gc-input" data-testid="nc-first-name" />
            </Field>
            <Field label="Last name" required>
              <input required value={form.last_name} onChange={set("last_name")} className="gc-input" data-testid="nc-last-name" />
            </Field>
            <Field label="Email" required span2>
              <input required type="email" value={form.email} onChange={set("email")} className="gc-input" data-testid="nc-email" placeholder="jane@business.ie" />
            </Field>
            <Field label="Phone" required>
              <input required value={form.phone} onChange={set("phone")} className="gc-input" data-testid="nc-phone" placeholder="087 123 4567" />
            </Field>
            <Field label="Business name" required>
              <input required value={form.business_name} onChange={set("business_name")} className="gc-input" data-testid="nc-business-name" />
            </Field>
            <Field label="VAT number">
              <input value={form.vat_number} onChange={set("vat_number")} className="gc-input" data-testid="nc-vat" placeholder="IE1234567X (optional)" />
            </Field>
          </div>

          {/* Address */}
          <div>
            <div className="eyebrow mb-3">Address</div>
            <div className="grid grid-cols-2 gap-5">
              <Field label="Address line 1" required span2>
                <input required value={form.address_line1} onChange={set("address_line1")} className="gc-input" data-testid="nc-addr1" />
              </Field>
              <Field label="Address line 2" span2>
                <input value={form.address_line2} onChange={set("address_line2")} className="gc-input" data-testid="nc-addr2" />
              </Field>
              <Field label="Town" required>
                <input required value={form.town} onChange={set("town")} className="gc-input" data-testid="nc-town" />
              </Field>
              <Field label="County" required>
                <select required value={form.county} onChange={set("county")} className="gc-input" data-testid="nc-county">
                  {counties.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="Eircode" required>
                <input required value={form.eircode} onChange={set("eircode")} className="gc-input uppercase" data-testid="nc-eircode" placeholder="V15 XXXX" />
              </Field>
            </div>
          </div>

          {/* Options */}
          <label className="flex items-start gap-3 cursor-pointer border border-[var(--gc-border)] p-4 hover:bg-[var(--gc-bg-alt)] transition-colors">
            <input
              type="checkbox"
              checked={form.send_welcome_email}
              onChange={setBool("send_welcome_email")}
              className="mt-1 accent-[var(--gc-orange)]"
              data-testid="nc-send-welcome"
            />
            <div className="text-sm">
              <div className="font-semibold flex items-center gap-2">
                <EnvelopeSimple size={16} weight="bold" />
                Send welcome email
              </div>
              <div className="text-[var(--gc-text-muted)] mt-1">
                Emails a &ldquo;Set your password&rdquo; link (valid 7 days) so the customer can activate their account and sign in. The template is editable in <em>Settings → Email Templates → Welcome</em>.
              </div>
            </div>
          </label>

          {/* First project brief (optional) */}
          <div className={`border p-4 transition-colors ${form.create_draft_job ? "border-[var(--gc-orange)] bg-orange-50/40" : "border-[var(--gc-border)]"}`}>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.create_draft_job}
                onChange={setBool("create_draft_job")}
                className="mt-1 accent-[var(--gc-orange)]"
                data-testid="nc-create-draft"
              />
              <div className="text-sm">
                <div className="font-semibold flex items-center gap-2">
                  <Briefcase size={16} weight="bold" />
                  Create first project brief now
                </div>
                <div className="text-[var(--gc-text-muted)] mt-1">
                  Spawn a job pre-linked to this customer so it appears in their portal the moment they sign in. Starts in <strong>Quote</strong> phase — ready for you to upload a quote.
                </div>
              </div>
            </label>

            {form.create_draft_job && (
              <div className="grid grid-cols-2 gap-5 mt-5 pl-8">
                <Field label="Project type" required>
                  <select
                    required={form.create_draft_job}
                    value={form.draft_project_type}
                    onChange={set("draft_project_type")}
                    className="gc-input"
                    data-testid="nc-draft-type"
                  >
                    <option value="">— Select a type —</option>
                    {projectTypes.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Project name" required>
                  <input
                    required={form.create_draft_job}
                    value={form.draft_project_name}
                    onChange={set("draft_project_name")}
                    className="gc-input"
                    data-testid="nc-draft-name"
                    placeholder="e.g. Rebrand 2026"
                  />
                </Field>
              </div>
            )}
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-[var(--gc-border)] px-8 py-5 flex items-center justify-between gap-3">
          <button type="button" onClick={onClose} className="text-sm link-underline text-[var(--gc-text-muted)]" data-testid="new-customer-cancel">Cancel</button>
          <button type="submit" disabled={loading} className="btn-solid disabled:opacity-50" data-testid="new-customer-submit">
            {loading ? "Creating…" : <>Create customer <ArrowUpRight size={16} weight="bold" /></>}
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, required, span2, children }) {
  return (
    <div className={span2 ? "col-span-2" : ""}>
      <div className="eyebrow mb-2">
        {label}{required && <span className="text-[var(--gc-orange)] ml-1">*</span>}
      </div>
      {children}
    </div>
  );
}
